import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { type Recording } from "@shared/schema";
import { ArrowLeft, Clock, User, Globe, Tag, Video, Calendar, AlertCircle, RefreshCw } from "lucide-react";
import { RECORDING_THUMBNAIL } from "@/lib/constants";
import { format } from "date-fns";

type RecordingDetail = Recording & {
  classTitle: string;
  providerName: string;
  providerId: string | null;
  language: string | null;
  category: string | null;
};

function formatDuration(seconds: number | null): string {
  if (!seconds) return "--:--";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function RecordingDetailPage() {
  const { t } = useTranslation();
  const { recordingId } = useParams<{ recordingId: string }>();
  const [, navigate] = useLocation();
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const [retryKey, setRetryKey] = useState(0);

  const handleVideoError = useCallback(() => {
    setVideoError(true);
    setVideoLoading(false);
  }, []);

  const handleRetry = useCallback(() => {
    setVideoError(false);
    setVideoLoading(true);
    setRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    setVideoError(false);
    setVideoLoading(true);
  }, [recordingId]);

  const { data: recording, isLoading, error } = useQuery<RecordingDetail>({
    queryKey: ["/api/recordings", recordingId],
    enabled: !!recordingId,
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-6">
        <Skeleton className="h-8 w-32 mb-6" />
        <Skeleton className="aspect-video w-full rounded-xl mb-4" />
        <Skeleton className="h-6 w-2/3 mb-2" />
        <Skeleton className="h-4 w-1/3" />
      </div>
    );
  }

  if (error || !recording) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-20 text-center">
        <div className="glass-panel depth-shadow-md p-10 inline-block mx-auto">
          <div className="empty-state-icon mb-4">
            <AlertCircle className="h-12 w-12 text-primary/25" />
          </div>
          <h2 className="text-lg font-semibold text-muted-foreground glow-text-sm">Recording not found</h2>
          <div className="divider-glow w-20 mx-auto my-4" />
          <Button variant="outline" className="mt-2" onClick={() => navigate("/recordings")} data-testid="button-back-library">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("recordings.title")}
          </Button>
        </div>
      </div>
    );
  }

  const thumbnailUrl = recording.thumbnailUrl || RECORDING_THUMBNAIL;
  const hasVideo = !!recording.url;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <div className="glass-page-bg" />
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate("/recordings")}
        data-testid="button-back"
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        {t("recordings.title")}
      </Button>

      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl glass-panel depth-shadow-lg" style={{ padding: 0 }}>
          {hasVideo && !videoError ? (
            <div className="relative">
              {recording.status === "PARTIAL" && (
                <div className="absolute top-0 left-0 right-0 z-20 bg-orange-500/90 text-white text-xs px-3 py-1.5 text-center" data-testid="banner-partial-recording">
                  <AlertCircle className="inline h-3 w-3 mr-1 -mt-0.5" />
                  Partial recording — some content may be missing
                </div>
              )}
              {videoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-white/60">Loading video...</p>
                  </div>
                </div>
              )}
              <video
                key={retryKey}
                src={`/api/recordings/${recording.id}/stream`}
                controls
                preload="metadata"
                className="w-full aspect-video bg-black"
                poster={thumbnailUrl}
                onLoadedData={() => setVideoLoading(false)}
                onError={handleVideoError}
                data-testid="video-player"
              />
            </div>
          ) : hasVideo && videoError ? (
            <div className="relative aspect-video w-full bg-black/80 flex flex-col items-center justify-center text-white text-center p-6">
              <AlertCircle className="h-12 w-12 mb-4 text-red-400/60" />
              <h3 className="text-lg font-semibold mb-2">Video failed to load</h3>
              <p className="text-sm text-white/60 max-w-md mb-4">
                The recording file could not be played. It may still be processing or the file format is not supported by your browser.
              </p>
              <Button variant="outline" size="sm" onClick={handleRetry} data-testid="button-retry-video">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          ) : (
            <div className="relative aspect-video w-full bg-black/80">
              <img
                src={thumbnailUrl}
                alt={recording.title || recording.classTitle}
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                <Video className="h-16 w-16 mb-4 opacity-40" />
                {recording.status === "READY" ? (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Recording Ready</h3>
                    <p className="text-sm text-white/60 max-w-md">
                      This session was recorded successfully but the video file is not available. It may still be uploading or was recorded before cloud storage was enabled.
                    </p>
                  </>
                ) : recording.status === "RECORDING" ? (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Recording in progress...</h3>
                    <p className="text-sm text-white/60 max-w-md">
                      This session is currently being recorded. The video will appear here once the session ends.
                    </p>
                  </>
                ) : recording.status === "PARTIAL" ? (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Partial Recording</h3>
                    <p className="text-sm text-white/60 max-w-md">
                      This recording was partially recovered from saved chunks. Some content may be missing. No playable file is available yet.
                    </p>
                  </>
                ) : recording.status === "FAILED" ? (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Recording Failed</h3>
                    <p className="text-sm text-white/60 max-w-md">
                      This recording could not be saved. The upload may have failed or been interrupted.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold mb-2">Uploading...</h3>
                    <p className="text-sm text-white/60 max-w-md">
                      This recording is being uploaded to cloud storage. Please wait.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <h1 className="text-xl font-bold sm:text-2xl glow-text" data-testid="text-recording-title">
            {recording.title || recording.classTitle}
          </h1>

          <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5" data-testid="text-provider">
              <User className="h-4 w-4" />
              <span>{recording.providerName}</span>
            </div>
            {recording.language && (
              <div className="flex items-center gap-1.5">
                <Globe className="h-4 w-4" />
                <span>{recording.language}</span>
              </div>
            )}
            {recording.durationSeconds && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                <span>{formatDuration(recording.durationSeconds)}</span>
              </div>
            )}
            {recording.startedAt && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                <span>{format(new Date(recording.startedAt), "MMM d, yyyy 'at' h:mm a")}</span>
              </div>
            )}
          </div>

          {recording.category && (
            <Badge variant="secondary" className="no-default-hover-elevate">
              {recording.category}
            </Badge>
          )}

          {recording.tags && recording.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <Tag className="h-4 w-4 text-muted-foreground mt-0.5" />
              {recording.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="no-default-hover-elevate">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          <div className="divider-glow-static w-full my-2" />

          {recording.description && (
            <div className="mt-4 glass-panel-sm depth-shadow-sm p-4">
              <p className="text-sm leading-relaxed text-muted-foreground" data-testid="text-description">
                {recording.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
